import { FC, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { Alert, Button, Heading, SkeletonText, SpacingToken, Text } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { StyledLink } from "components/links/StyledLink";
import { SectionOnboardingBanner } from "components/section-onboarding-banner/SectionOnboardingBanner";
import {
  SettingsTableColumn as Column,
  SettingsTableRow as Row,
  SettingsTable as Table,
} from "components/settings-table";
import Role from "constants/role";
import { EventPlace, trackEngagement } from "helpers/analytics";
import useModal from "hooks/use-modal";
import { useToggle } from "hooks/use-toggle";
import { selectLoggedInAgentPermission } from "store/entities/agents";
import { selectContactFormsSorted } from "store/entities/contact-forms/selectors";
import { selectHasLiveChatIntegration, selectLivechatGroupsDataStatus } from "store/entities/integrations";
import { DataStatus, selectDefaultTeamId, selectLicenseId } from "store/entities/license";
import { selectTeams } from "store/entities/teams";
import { withVar } from "themes/helpers";

import SettingsViewContainer from "../ViewContainer";
import { SettingsRoutes } from "../constants";
import { ContentWrapper, PageWrapper } from "../styled";

import { ContactFormChooser } from "./ContactFormChooser";
import { ContactFormRow } from "./ContactFormRow";
import { DeleteContactForm } from "./DeleteContactForm";
import { DEFAULT_LIVECHAT_CONTACT_FORM_ID, DEFAULT_SETTINGS } from "./constants";
import { ContactFormsEmptyState } from "./legacy/ContactFormsEmptyState";

export const ContactFormsList: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [deleteContactFormID, { show: showDeleteModal, close: closeDeleteModal }] = useModal<string | null>(null);
  const [contactFormChooserModal, toggleContactFormChooserModal] = useToggle(false);
  const contactForms = useSelector(selectContactFormsSorted);
  const teams = useSelector(selectTeams);
  const defaultTeamID = useSelector(selectDefaultTeamId);
  const isAdmin = useSelector(selectLoggedInAgentPermission) === Role.Owner;
  const livechatGroupsDataStatus = useSelector(selectLivechatGroupsDataStatus);
  const licenseID = useSelector(selectLicenseId);
  const hasLivechatIntegration = livechatGroupsDataStatus === DataStatus.Ready;
  const isLiveChatIntegrationEnabled = useSelector(selectHasLiveChatIntegration);
  const isLoadingData = livechatGroupsDataStatus === DataStatus.Loading;
  const columnsCount = hasLivechatIntegration ? 4 : 3;
  const columnSizes = hasLivechatIntegration ? "1fr 1fr 1fr 48px" : "1fr 1fr 48px";
  const columnHeaders = hasLivechatIntegration ? ["Name", "Teams", "LiveChat groups", ""] : ["Name", "Teams", ""];
  const deleteContactForm = contactForms.find(form => form.id === deleteContactFormID);
  const shouldShowEmptyState = contactForms.length === 0 && !hasLivechatIntegration && !isLoadingData;

  const handleNewFormClick = (): void => {
    if (isLiveChatIntegrationEnabled) {
      toggleContactFormChooserModal();
    } else {
      handleNew("form");
    }
  };

  const handleNew = (editorType: "form" | "widget"): void => {
    if (editorType === "form") {
      history.push(`${SettingsRoutes.ContactForms}/add`);
    } else {
      history.push(`${SettingsRoutes.WidgetForms}/add`);
    }
    trackEngagement("Settings: Contact forms creation started", EventPlace.Settings, {
      formType: editorType == "form" ? "helpdesk" : "livechat",
    });
  };

  const handleEdit = useCallback(
    (id: string, openOldBuilder?: boolean) => {
      if (isAdmin) {
        if (!openOldBuilder) {
          history.push(`${SettingsRoutes.ContactForms}/edit/${id}`);
        } else {
          history.push(`${SettingsRoutes.WidgetForms}/edit/${id}`);
        }
        trackEngagement("Settings: Contact forms editing started", EventPlace.Settings);
      }
    },
    [history, isAdmin]
  );

  const handleDuplicate = useCallback(
    (id: string, openOldBuilder?: boolean) => {
      if (isAdmin) {
        if (!openOldBuilder) {
          history.push(`${SettingsRoutes.ContactForms}/duplicate/${id}`);
        } else {
          history.push(`${SettingsRoutes.WidgetForms}/duplicate/${id}`);
        }
        trackEngagement("Settings: Contact forms duplication started", EventPlace.Settings);
      }
    },
    [history, isAdmin]
  );

  const handleDelete = useCallback(
    (id: string) => {
      showDeleteModal(id);
      trackEngagement("Settings: Contact forms deletion modal shown", EventPlace.Settings);
    },
    [showDeleteModal]
  );

  useEffect(() => {
    trackEngagement("Settings: Contact form section opened", EventPlace.Settings);
  }, []);

  useEffect(() => {
    if ((location.hash || "").includes("#livechatGroupID")) {
      const [, lcGroupID] = location.hash.split("=");

      const targetFormID = contactForms.find(form => form.livechatGroupIDs.includes(lcGroupID))?.id || "default";
      history.replace(`/settings/contact-forms/${targetFormID}`);
    }
  }, [contactForms, history, location]);

  return (
    <>
      {deleteContactFormID !== null && deleteContactForm?.id && (
        <DeleteContactForm
          id={deleteContactForm.id}
          name={deleteContactForm.name}
          onClose={closeDeleteModal}
          isWordpress={deleteContactForm.wordpressContactForm}
        />
      )}
      <SettingsViewContainer title="Contact forms">
        <PageWrapper>
          <ContentWrapper>
            <SectionOnboardingBanner section="contact forms" />
            <Heading as="h2" size="xs">
              Contact Forms
            </Heading>
            <Text css={{ marginBottom: withVar(SpacingToken.Spacing3) }}>
              Use the built-in editor to customize your contact form and assign a dedicated team to it. Add the form to
              your website to start receiving tickets.{" "}
            </Text>
            <Button
              kind="primary"
              type="button"
              onClick={handleNewFormClick}
              data-testid="create-contact-form-main"
              disabled={!isAdmin}
            >
              Create contact form
            </Button>
            {!isAdmin && (
              <Block maxWidth="800px" marginV="42px">
                <Alert kind="info" data-testid="alert-component">
                  You are not allowed to manage contact forms.{" "}
                  <StyledLink kind="link-light" to="/agents">
                    Contact one of your Admins
                  </StyledLink>{" "}
                  to do so.
                </Alert>
              </Block>
            )}
            <Block marginTop={withVar(SpacingToken.Spacing10)}>
              {!shouldShowEmptyState && (
                <Table columns={columnsCount} columnSizes={columnSizes} headers={columnHeaders}>
                  {isLoadingData && (
                    <Row>
                      <Column>
                        <SkeletonText />
                      </Column>
                      <Column>
                        <SkeletonText />
                      </Column>
                      <Column />
                      {hasLivechatIntegration && <Column />}
                    </Row>
                  )}
                  {contactForms.map(form => (
                    <ContactFormRow
                      key={form.id}
                      {...form}
                      teams={teams}
                      defaultTeamID={defaultTeamID}
                      isAdmin={isAdmin}
                      hasLivechatIntegration={hasLivechatIntegration}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onDuplicate={handleDuplicate}
                    />
                  ))}
                  {hasLivechatIntegration && (
                    <ContactFormRow
                      {...DEFAULT_SETTINGS}
                      name="Default form for widget"
                      id={DEFAULT_LIVECHAT_CONTACT_FORM_ID}
                      licenseID={licenseID}
                      teams={teams}
                      teamID={defaultTeamID}
                      defaultTeamID={defaultTeamID}
                      isAdmin={isAdmin}
                      hasLivechatIntegration={hasLivechatIntegration}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onDuplicate={handleDuplicate}
                    />
                  )}
                </Table>
              )}
              {shouldShowEmptyState && <ContactFormsEmptyState onClick={handleNewFormClick} />}
            </Block>
          </ContentWrapper>
        </PageWrapper>
        {contactFormChooserModal && (
          <ContactFormChooser onClose={toggleContactFormChooserModal} onEditorClick={handleNew} />
        )}
      </SettingsViewContainer>
    </>
  );
};
